import { type DynamicWidgetActionState } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_actions';
import {
    type DynamicWidgetIssue,
    getDynamicWidgetSourceId,
    type ShiftListWidgetModel,
    type ShiftListWidgetRow,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    getActionableShiftRows,
    isActionableShiftStatus,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_list_model';
import { type StatusIntent } from '@apps/web/components/ui/status_tokens';

export const SHIFT_LIST_COLLAPSED_ROW_LIMIT = 12;

const SHIFT_STATUS_DISPLAY: Record<
    string,
    { readonly label: string; readonly intent: StatusIntent }
> = {
    open: { label: 'Open', intent: 'neutral' },
    not_scheduled: { label: 'Not scheduled', intent: 'neutral' },
    needs_coverage: { label: 'Needs coverage', intent: 'negative' },
    needs_attention: { label: 'Needs attention', intent: 'negative' },
    pending_confirmation: {
        label: 'Pending confirmation',
        intent: 'warning',
    },
    assigned: { label: 'Assigned', intent: 'info' },
    in_progress: { label: 'In progress', intent: 'info' },
    assigned_and_confirmed: { label: 'Confirmed', intent: 'positive' },
    confirmed: { label: 'Confirmed', intent: 'positive' },
    declined: { label: 'Declined', intent: 'negative' },
    no_response: { label: 'No response', intent: 'warning' },
    needs_review: { label: 'Needs review', intent: 'negative' },
    clock_in_reminder: { label: 'Clock-in reminder', intent: 'warning' },
    clock_out_reminder: { label: 'Clock-out reminder', intent: 'warning' },
    confirmation_follow_up: {
        label: 'Confirmation follow-up',
        intent: 'warning',
    },
    completed: { label: 'Completed', intent: 'positive' },
    cancelled: { label: 'Cancelled', intent: 'neutral' },
    missing: { label: 'Removed from EHR', intent: 'neutral' },
    unknown: { label: 'Unknown', intent: 'neutral' },
};

export type ShiftListDateFilter = 'all' | 'today' | 'next_week';

export type ShiftListFilters = {
    readonly date: ShiftListDateFilter;
    readonly client: string | null;
    readonly location: string | null;
};

export type ShiftRowViewModel = {
    readonly row: ShiftListWidgetRow;
    readonly rowIndex: number;
    readonly statusDisplay: {
        readonly label: string;
        readonly intent: StatusIntent;
    } | null;
    readonly statusIssues: ReadonlyArray<DynamicWidgetIssue>;
    readonly otherFieldIssues: ReadonlyArray<DynamicWidgetIssue>;
    readonly actionIssues: ReadonlyArray<DynamicWidgetIssue>;
    readonly isOpenShift: boolean;
};

export type ShiftDayGroupViewModel = {
    readonly day: string | null;
    readonly dayLabel: string;
    readonly groupIndex: number;
    readonly rows: ReadonlyArray<ShiftRowViewModel>;
};

export type ShiftListViewModel = {
    readonly clientOptions: ReadonlyArray<string>;
    readonly locationOptions: ReadonlyArray<string>;
    readonly dayGroups: ReadonlyArray<ShiftDayGroupViewModel>;
    readonly hiddenCount: number;
    readonly widgetIssues: ReadonlyArray<DynamicWidgetIssue>;
    readonly contentIssues: ReadonlyArray<DynamicWidgetIssue>;
    readonly bulkRows: ReadonlyArray<ShiftListWidgetRow>;
    readonly bulkActionIssues: ReadonlyArray<DynamicWidgetIssue>;
    readonly showHeaderCount: boolean;
};

function distinctValues(values: ReadonlyArray<string | null>): Array<string> {
    return Array.from(
        new Set(
            values.filter(
                (value): value is string => value !== null && value.length > 0,
            ),
        ),
    );
}

function getBindingIssues(
    issues: ReadonlyArray<DynamicWidgetIssue>,
    bindingIds: ReadonlyArray<string>,
): Array<DynamicWidgetIssue> {
    const ids = new Set(bindingIds);
    return issues.filter(
        (issue) =>
            issue.scope === 'field' &&
            issue.bindingIds.some((bindingId) => ids.has(bindingId)),
    );
}

function buildShiftRowViewModel(
    model: ShiftListWidgetModel,
    rowIndex: number,
): ShiftRowViewModel | null {
    const row = model.rows[rowIndex];
    if (row === undefined) {
        return null;
    }
    const rowBindingPrefix = `rows.${rowIndex}.`;
    return {
        row,
        rowIndex,
        statusDisplay: getShiftStatusDisplay(row.status),
        statusIssues: getBindingIssues(model.issues, [
            `${rowBindingPrefix}status`,
        ]),
        otherFieldIssues: model.issues.filter(
            (issue) =>
                issue.scope === 'field' &&
                issue.bindingIds.some(
                    (bindingId) =>
                        bindingId.startsWith(rowBindingPrefix) &&
                        bindingId !== `${rowBindingPrefix}status`,
                ),
        ),
        actionIssues: model.issues.filter(
            (issue) =>
                issue.scope === 'action' &&
                issue.bindingIds.some((bindingId) =>
                    bindingId.startsWith(rowBindingPrefix),
                ),
        ),
        isOpenShift: isActionableShiftStatus(row.status),
    };
}

export function organizationLocalToday(timezone: string): string {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(new Date());
}

function nextWeekDateRange(today: string): {
    readonly start: string;
    readonly end: string;
} | null {
    const parsedToday = new Date(`${today}T00:00:00Z`);
    if (Number.isNaN(parsedToday.getTime())) {
        return null;
    }
    // Shift filters use Monday-through-Sunday calendar weeks, so a click
    // never mixes the remainder of this week into "Next week."
    const currentIsoWeekday = parsedToday.getUTCDay() || 7;
    const daysUntilNextMonday = 8 - currentIsoWeekday;
    const start = new Date(parsedToday);
    start.setUTCDate(start.getUTCDate() + daysUntilNextMonday);
    const end = new Date(start);
    end.setUTCDate(end.getUTCDate() + 6);
    return {
        start: start.toISOString().slice(0, 10),
        end: end.toISOString().slice(0, 10),
    };
}

function matchesDateFilter(
    day: string | null,
    filter: ShiftListDateFilter,
    today: string,
): boolean {
    if (filter === 'all') {
        return true;
    }
    if (filter === 'today') {
        return day === today;
    }
    const nextWeek = nextWeekDateRange(today);
    return (
        day !== null &&
        nextWeek !== null &&
        day >= nextWeek.start &&
        day <= nextWeek.end
    );
}

export function getShiftStatusDisplay(status: string | null): {
    readonly label: string;
    readonly intent: StatusIntent;
} | null {
    if (!status) {
        return null;
    }
    return (
        SHIFT_STATUS_DISPLAY[status] ?? {
            label: status.split('_').join(' '),
            intent: 'neutral',
        }
    );
}

export function formatShiftDayHeading(
    day: string | null,
    fallbackLabel: string,
): string {
    if (!day) {
        return fallbackLabel;
    }
    const parsed = new Date(`${day}T00:00:00Z`);
    if (Number.isNaN(parsed.getTime())) {
        return fallbackLabel;
    }
    const weekday = parsed.toLocaleDateString('en-US', {
        weekday: 'long',
        timeZone: 'UTC',
    });
    const monthDay = parsed.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
    });
    return `${weekday} · ${monthDay}`;
}

export function shiftReferences(shiftIds: ReadonlyArray<string>): string {
    return shiftIds.map((shiftId) => `[[shift:${shiftId}]]`).join(', ');
}

export function actionButtonLabel(
    state: DynamicWidgetActionState,
    idleLabel: string,
): string {
    switch (state.status) {
        case 'idle':
            return idleLabel;
        case 'submitted':
        case 'awaiting_approval':
        case 'running':
            return 'Request sent';
        case 'succeeded':
            return 'Outreach created';
        case 'failed':
            return 'Request failed';
        case 'superseded':
            return 'Request replaced';
        default: {
            const exhaustiveStatus: never = state.status;
            return exhaustiveStatus;
        }
    }
}

export function actionButtonDisabled(
    state: DynamicWidgetActionState,
    isSubmitting: boolean,
): boolean {
    return (
        isSubmitting ||
        state.status === 'submitted' ||
        state.status === 'awaiting_approval' ||
        state.status === 'running' ||
        state.status === 'succeeded'
    );
}

export function getActionIssue(
    model: ShiftListWidgetModel,
    state: DynamicWidgetActionState,
    bindingIds: ReadonlyArray<string>,
): DynamicWidgetIssue | null {
    if (state.status !== 'failed') {
        return null;
    }
    const code = state.errorCode === null ? 'action_failed' : 'action_rejected';
    return {
        id: `${state.action?.id ?? model.widgetId}.${code}`,
        code,
        level: code === 'action_failed' ? 'error' : 'warning',
        scope: 'action',
        bindingIds,
        sourceIds: model.sources.map(getDynamicWidgetSourceId),
        retryable: false,
        safeDetails: {
            errorCode: state.errorCode,
            actionId: state.action?.id ?? null,
            actionStatus: state.status,
        },
    };
}

export function buildShiftListViewModel({
    model,
    filters,
    today,
    showAllShifts,
}: {
    readonly model: ShiftListWidgetModel;
    readonly filters: ShiftListFilters;
    readonly today: string;
    readonly showAllShifts: boolean;
}): ShiftListViewModel {
    const filteredRowIndexes = model.rows
        .map((row, rowIndex) => ({ row, rowIndex }))
        .filter(
            ({ row }) =>
                matchesDateFilter(row.day, filters.date, today) &&
                (filters.client === null ||
                    row.patientName === filters.client) &&
                (filters.location === null || row.city === filters.location),
        )
        .map(({ rowIndex }) => rowIndex);
    const visibleRowIndexes = showAllShifts
        ? filteredRowIndexes
        : filteredRowIndexes.slice(0, SHIFT_LIST_COLLAPSED_ROW_LIMIT);
    const visibleRowIndexSet = new Set(visibleRowIndexes);
    const dayGroups = model.dayGroups.flatMap((group, groupIndex) => {
        const rows = group.rowIndexes
            .filter((rowIndex) => visibleRowIndexSet.has(rowIndex))
            .flatMap((rowIndex) => {
                const row = buildShiftRowViewModel(model, rowIndex);
                return row === null ? [] : [row];
            });
        return rows.length === 0 ? [] : [{ ...group, groupIndex, rows }];
    });
    const sectionIssues = model.issues.filter(
        (issue) => issue.scope === 'section',
    );
    const unplacedFieldIssues = model.issues.filter(
        (issue) =>
            issue.scope === 'field' &&
            issue.bindingIds.every(
                (bindingId) => !bindingId.startsWith('rows.'),
            ),
    );
    return {
        clientOptions: distinctValues(model.rows.map((row) => row.patientName)),
        locationOptions: distinctValues(model.rows.map((row) => row.city)),
        dayGroups,
        hiddenCount: filteredRowIndexes.length - visibleRowIndexes.length,
        widgetIssues: model.issues.filter((issue) => issue.scope === 'widget'),
        contentIssues: [...sectionIssues, ...unplacedFieldIssues],
        bulkRows: getActionableShiftRows(model.rows).filter((row) =>
            row.occurrenceIds.some((shiftId) =>
                model.bulkShiftIds.includes(shiftId),
            ),
        ),
        bulkActionIssues: model.issues.filter(
            (issue) =>
                issue.scope === 'action' &&
                issue.bindingIds.includes('bulkShiftIds'),
        ),
        showHeaderCount: model.templateVersion === 3 && model.showHeaderCount,
    };
}
